import { useState } from "react";
import { useNavigate, useLocation } from "react-router";
import { navigationItems } from "@/constants/index";
import { Tab } from "./tab";
import { Cursor } from "./cursor";
import { NavDropdown } from "./nav-dropdown";
import { Sidebar } from "./sidebar/sidebar";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { useTheme } from "@/components/providers/theme-provider";
import { ArrowButton } from "@/components/ui/arrow-button";
import { useNavigationStore } from "@/stores/navigationStore";
import { scrollToElement, scrollToTop } from "@/lib/scroll";
import type { Position } from "./types";

export function NavBar() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [position, setPosition] = useState<Position>({
    left: 0,
    width: 0,
    opacity: 0,
  });

  // Get active navigation item using optimized selector
  const activeNavigationItem = useNavigationStore(state => state.getActiveNavigationItem());

  // Logo click: from the landing route, scroll to the hero (#home).
  // From any other route, navigate back to "/" with #home so
  // <ScrollToHash /> lands the user on the hero after mount.
  const handleLogoClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    if (pathname === "/") {
      if (!scrollToElement("home")) scrollToTop();
      return;
    }
    navigate("/#home");
  };

  return (
    <>
      {/* Desktop Navigation - Hidden on mobile/tablet, visible on xl+ */}
      <nav className="fixed top-7 left-1/2 -translate-x-1/2 z-50 hidden xl:block">
        <ul
          onMouseLeave={() => {
            setPosition((pv) => ({
              ...pv,
              opacity: 0,
            }));
          }}
          className="glass-texture rounded-full flex items-center justify-center gap-2 px-4 py-2 relative w-max"
        >
          {/* Logo */}
          <li className="flex items-center shrink-0 mr-4">
            <a
              href="/#home"
              onClick={handleLogoClick}
              aria-label="Go to home"
              className="relative z-10 flex items-center px-3 py-1.5 rounded-lg hover:bg-accent/50 transition-colors"
            >
              <img
                src={(() => {
                  const effectiveTheme = theme === 'system'
                    ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
                    : theme;

                  return effectiveTheme === 'dark'
                    ? "/poly186-logo-white.png"
                    : "/poly186-logo-purple.png";
                })()}
                alt="Poly186"
                className="h-6 w-auto object-contain"
              />
            </a>
          </li>

          {/* Separator */}
          <li className="h-5 w-px bg-border/50" aria-hidden="true" />

          {navigationItems.map((item) =>
            item.dropdown ? (
              <NavDropdown key={item.name} item={item} />
            ) : (
              <Tab
                key={item.name}
                setPosition={setPosition}
                href={item.link}
                isActive={activeNavigationItem === item.name}
              >
                {item.name}
              </Tab>
            )
          )}

          {/* Theme Toggle */}
          <li className="ml-2">
            <ThemeToggle />
          </li>

          {/* Login Button */}
          <li>
            <a
              href="https://poly.agencyofpoly.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              <ArrowButton>login</ArrowButton>
            </a>
          </li>

          <Cursor position={position} />
        </ul>
      </nav>

      <Sidebar />
    </>
  );
}